import React, { useState } from 'react';
import { Modal, Checkbox, Button, Typography, Divider, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { makeStyles } from '@material-ui/core';
import {
  PLAYER_CSV_COLUMNS,
  DEFAULT_PLAYER_CSV_COLUMNS,
  playersToCsv,
  playerCsvFilename,
  downloadCsv,
} from '../../../utilities';
import { adminModal } from './adminStyles';

const { Text } = Typography;

const useStyles = makeStyles((theme) => ({
  modal: adminModal(theme),
  columnList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '10px 16px',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
    // antd indents every checkbox after the first, which knocks the grid out of line.
    '& .ant-checkbox-wrapper.ant-checkbox-wrapper': {
      marginLeft: 0,
    },
  },
  summary: {
    marginTop: 16,
    display: 'block',
  },
}));

export default function ExportPlayersModal({ visible, onCancel, players }) {
  const styles = useStyles();
  const [selected, setSelected] = useState(DEFAULT_PLAYER_CSV_COLUMNS);

  const allKeys = PLAYER_CSV_COLUMNS.map(({ key }) => key);
  const allSelected = selected.length === allKeys.length;

  const handleSelectAll = (e) => {
    setSelected(e.target.checked ? allKeys : []);
  };

  const handleDownload = () => {
    const csv = playersToCsv(players, selected);

    if (!csv) {
      return message.warning('Pick at least one column to export.');
    }

    downloadCsv(csv, playerCsvFilename());
    message.success(`Exported ${players.length} player${players.length === 1 ? '' : 's'}.`);
    onCancel();
  };

  return (
    <Modal
      title="Export players"
      visible={visible}
      onCancel={onCancel}
      className={styles.modal}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          disabled={!selected.length || !players.length}
          onClick={handleDownload}
        >
          Download CSV
        </Button>,
      ]}
      destroyOnClose={false}
    >
      <Checkbox
        checked={allSelected}
        indeterminate={!allSelected && selected.length > 0}
        onChange={handleSelectAll}
      >
        <Text strong>Select all columns</Text>
      </Checkbox>

      <Divider style={{ margin: '12px 0' }} />

      <Checkbox.Group value={selected} onChange={setSelected} style={{ width: '100%' }}>
        <div className={styles.columnList}>
          {PLAYER_CSV_COLUMNS.map(({ key, label }) => (
            <Checkbox key={key} value={key}>{label}</Checkbox>
          ))}
        </div>
      </Checkbox.Group>

      <Text type="secondary" className={styles.summary}>
        {players.length} player{players.length === 1 ? '' : 's'} and {selected.length} column
        {selected.length === 1 ? '' : 's'} will be exported. The whole roster is included, even
        when the table is filtered by a search.
      </Text>
    </Modal>
  );
}
